/**
 * Unified backend API utilities.
 * All API calls to the backend are centralized here.
 */

import { AuthContextProps } from 'react-oidc-context';
import { getApiAuthHeaders } from './authHelpers';
import config from '../config.json';

// --- Types ---

export interface SessionCreateRequest {
  goal: string;
  user_id: string;
  group_id: string;
  template_id?: string;
  mode?: 'guided' | 'interrogation';
}

// Plan items can be either strings or objects with {id, task, ...metadata}
export type PlanItemObject = {
  id: number | string;
  task: string;
  status?: string;
  user_output?: string;
  image_analysis?: string;
  started_at?: string;      // ISO-8601 UTC, set when the step first becomes current
  completed_at?: string;    // ISO-8601 UTC, set when the step is APPROVED
  duration_seconds?: number; // end - start
  completed_by?: string;    // user id / email
  /** Either a URL copied verbatim from the AUTHORITATIVE REFERENCE DOCUMENTATION
   *  block fed to the planning LLM, or the literal string 'GENERAL_KNOWLEDGE'
   *  when no authoritative page covers this step. Steps with
   *  'GENERAL_KNOWLEDGE' should be treated as LLM improvisation. */
  source?: string;
  /** Defaults to HIGH when omitted. LOW = LLM was unable to find authoritative
   *  guidance for this step (either because docs didn't cover it or because
   *  the fetch was blocked by a WAF). */
  confidence?: 'HIGH' | 'LOW' | string;
};

export interface GroundingRecord {
  status: 'ok' | 'partial' | 'none';
  urls_fetched?: string[];
  urls_failed?: string[];
  notes?: string;
  recorded_at?: string;
}
export type PlanItem = string | PlanItemObject;

/** One field in a clarification spec. Used by the FE to render a structured form
 *  instead of asking the user to answer a markdown blob as free text. */
export interface ClarificationField {
  key: string;
  question: string;
  required: boolean;
  /** Defaults to 'text' if omitted. */
  type?: 'text' | 'textarea' | 'select' | 'radio' | 'checkbox_group';
  /** Only for select / radio / checkbox_group. */
  options?: string[];
  /** Only for text / textarea. */
  placeholder?: string;
  /** Optional help text shown in a tooltip behind an info icon next to the
   *  question. Meant for explanations + concrete examples + why the field
   *  matters for the plan. Markdown is NOT supported — use plain text. */
  help?: string;
}

export interface SessionCreateResponse {
  session_id: string;
  status: 'CLARIFICATION_NEEDED' | 'PLANNING' | 'DRAFTING' | 'NEEDS_INPUT' | 'COMPLETED';
  mode?: 'guided' | 'interrogation';
  plan: PlanItem[];
  message?: string; // Clarification markdown (fallback if no structured spec)
  clarification_spec?: ClarificationField[];
}

export interface OntologyEntity {
  id: string;
  name: string;
  category: string;
  subtype?: string;
  aliases?: string[];
  notes?: string;
}

export interface OntologyRelation {
  id?: string;
  source: string;
  predicate: string;
  target: string;
  notes?: string;
  evidence?: string;
}

export interface InterrogationMemory {
  rolling_summary?: string;
  /** @deprecated legacy shape — use entities */
  entity_registry?: Array<{ name: string; type?: string; notes?: string }>;
  entities?: OntologyEntity[];
  relations?: OntologyRelation[];
  open_threads?: string[];
  claim_references?: Record<string, string[]>;
}

/** True when a session has reached a terminal state where a report may be viewed. */
export function isSessionComplete(status: string | undefined | null): boolean {
  const normalized = (status || '').toUpperCase();
  return normalized === 'COMPLETED' || normalized === 'INTERROGATING_COMPLETE';
}

export interface TranscriptSegment {
  sender?: string;
  message?: string;
  timestamp: string;
  speaker_label?: string;
  message_type?: string;
}

export interface LlmUsageFeatureSummary {
  feature: string;
  calls: number;
  cost_usd: number;
  total_tokens?: number;
}

export interface LlmUsageTimelinePoint {
  call_id?: string;
  ts?: string;
  feature?: string;
  model?: string;
  cost_usd?: number;
  cumulative_cost_usd?: number;
  total_tokens?: number;
  prompt_tokens?: number;
  output_tokens?: number;
  thinking_tokens?: number;
  turn_index?: number;
  chunk_index?: number;
  source_message_ts?: string;
  utterance_preview?: string;
}

export interface LlmUsageSummary {
  total_cost_usd: number;
  total_calls: number;
  total_tokens: number;
  by_feature?: LlmUsageFeatureSummary[];
  timeline?: LlmUsageTimelinePoint[];
}

export interface SessionStatusResponse {
  session_id: string;
  status: 'CLARIFICATION_NEEDED' | 'DRAFTING' | 'RUNNING' | 'NEEDS_INPUT' | 'LISTENING' | 'COMPLETED' | 'INTERROGATING_COMPLETE' | 'ERROR';
  mode?: 'guided' | 'interrogation';
  awaiting_user_input?: boolean;
  current_step_index?: number;
  current_question?: string;
  /** Most recent deterministic known-issue match for the user's last reply
   *  (regex hits against known_issues.json). Surfaced alongside
   *  current_question so the chat can render a diagnostic card next to the
   *  bot's reply. Empty/undefined when nothing matched. */
  current_known_issues?: KnownIssueMatch[];
  /** Timestamp of the source KNOWN_ISSUE message — used by the FE polling
   *  hook to dedup re-adds across polls. */
  current_known_issues_id?: string;
  messages?: Array<{ sender: string; message?: string; timestamp: string; attachments?: string[]; plan_placeholder?: boolean; message_type?: string; plan_snapshot?: any; known_issue_matches?: KnownIssueMatch[]; speaker_label?: string }>;
  history?: Array<{ role: string; content: string; attachments?: string[] }>;
  final_report?: string;
  interrogation_memory?: InterrogationMemory;
  live_transcript_tail?: TranscriptSegment[];
  speaker_registry?: Record<string, string>;
  interrogation_input_mode?: 'audio' | 'text' | 'hybrid';
  last_transcript_at?: string;
  last_question_at?: string;
  open_threads_count?: number;
  /** True once the session reaches COMPLETED — drives the report CTA in the
   *  chat. The structured report is fetched via getSessionReport() and
   *  rendered in /sessions/:id/report. */
  report_available?: boolean;
  report_url?: string;
  /** True for templates that produce a per-customer fillable installation
   *  document (Tableau install, Tableau tuning, ...). When false the FE
   *  uses generic "Session report" wording instead of engagement-specific
   *  wording. */
  report_is_customer_template?: boolean;
  /** Short noun the FE uses in the completion card title and button
   *  label, e.g. "Installation" or "Tuning". Empty/undefined for generic
   *  session reports. */
  report_kind?: string;
  plan?: PlanItem[];
  message?: string;
  clarification_spec?: ClarificationField[];
  clarification_attempts?: number;
  execution_started_at?: string;
  grounding?: GroundingRecord;
  extracted_facts?: Record<string, string>;
  llm_usage?: LlmUsageSummary;
  consolidation_status?: 'none' | 'pending' | 'running' | 'complete' | 'failed';
  consolidation_turn_index?: number;
}

export interface SessionReplyResponse {
  message: string;
  status: string;
  current_question?: string;
  /** Populated when the backend's clarification quality gate rejected the
   *  submission. Each entry names a field key and the reason it wasn't
   *  specific/plausible/consistent enough. FE renders these inline on the
   *  affected field so the user can see exactly what to fix. */
  quality_issues?: Array<{ key: string; problem: string; suggestion?: string }>;
  /** Raw list of required fields still missing (from the completeness gate). */
  missing?: string[];
  consolidation_status?: 'none' | 'pending' | 'running' | 'complete' | 'failed';
  consolidation_turn_index?: number;
}

export interface SessionListItem {
  session_id: string;
  goal: string;
  status: string;
  created_at: string;
}

export interface SessionsListResponse {
  sessions: SessionListItem[];
}

export interface TemplateListItem {
  template_id: string;
  title: string;
  goal_hint?: string;
}

export interface TemplatesListResponse {
  templates: TemplateListItem[];
}

export interface SessionDetails {
  session_id: string;
  status: 'RUNNING' | 'NEEDS_INPUT' | 'COMPLETED' | 'ERROR' | 'CLARIFICATION_NEEDED' | 'DRAFTING';
  mode?: 'guided' | 'interrogation';
  awaiting_user_input?: boolean;
  started_at: string;
  execution_started_at?: string;
  goal?: string;
  progress?: {
    done: number;
    total: number;
    current_step: number;
  };
  plan?: PlanItemObject[];
  messages?: Array<{ sender: string; message: string; timestamp: string; attachments?: string[] }>;
  clarification_spec?: ClarificationField[];
  clarification_attempts?: number;
  grounding?: GroundingRecord;
  extracted_facts?: Record<string, string>;
}

export interface RequirementsMatrixExcerpt {
  source_pdf?: string;
  source_pages?: number[];
  section_title?: string;
  section_level?: number;
  context: string;
  specs: Record<string, string[]>;
}

export interface RequirementsMatrix {
  schema_version?: number;
  description?: string;
  by_os: Record<string, RequirementsMatrixExcerpt[]>;
  stats?: Record<string, number>;
}

export interface ResourceWarning {
  field: 'ram_gb' | 'cores' | 'disk_gb' | 'ghz';
  user_value: number;
  doc_min_seen: number;
  message: string;
}

const TABLEAU_PRODUCTION_MINIMUMS = {
  ram_gb: 32,
  cores: 8,
  disk_gb: 50,
  ghz: 2.0,
};

// --- API Base URL ---

const getBaseUrl = () => {
  const url = config.api.chatbot.url;
  return url.endsWith('/') ? url.slice(0, -1) : url;
};

// --- Session API ---

/**
 * Create a new session with a goal.
 * POST /session
 */
export const createSession = async (
  goal: string,
  auth: AuthContextProps,
  template_id?: string,
  mode?: 'guided' | 'interrogation'
): Promise<SessionCreateResponse> => {
  const headers = getApiAuthHeaders(auth);
  const userEmail = auth.user?.profile?.email as string || 'unknown';
  const userGroups = auth.user?.profile?.['cognito:groups'] as string[] || [];
  
  const payload: SessionCreateRequest = {
    goal,
    user_id: userEmail,
    group_id: userGroups[0] || 'default',
  };

  if (template_id) {
    payload.template_id = template_id;
  }
  if (mode) {
    payload.mode = mode;
  }

  const response = await fetch(`${getBaseUrl()}/session`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to create session: ${response.status}`);
  }

  return response.json();
};

/**
 * Get session status (for polling).
 * GET /session/{session_id}
 */
export const getSessionStatus = async (
  sessionId: string,
  auth: AuthContextProps
): Promise<SessionStatusResponse> => {
  const headers = getApiAuthHeaders(auth);

  const response = await fetch(`${getBaseUrl()}/session/${sessionId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to get session status: ${response.status}`);
  }

  return response.json();
};

/**
 * Update/approve the plan.
 * PUT /session/{session_id}/plan
 */
export const updateSessionPlan = async (
  sessionId: string,
  plan: string[],
  status: 'APPROVED' | 'PLANNING',
  auth: AuthContextProps
): Promise<{ message: string; status: string }> => {
  const headers = getApiAuthHeaders(auth);

  const response = await fetch(`${getBaseUrl()}/session/${sessionId}/plan`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ plan, status }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to update plan: ${response.status}`);
  }

  return response.json();
};

/**
 * Start session execution.
 * POST /session/{session_id}/start
 */
export const startSession = async (
  sessionId: string,
  auth: AuthContextProps
): Promise<{ message: string; executionArn: string }> => {
  const headers = getApiAuthHeaders(auth);

  const response = await fetch(`${getBaseUrl()}/session/${sessionId}/start`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to start session: ${response.status}`);
  }

  return response.json();
};

/**
 * Reply to a session question.
 * POST /session/{session_id}/reply
 */
export const replyToSession = async (
  sessionId: string,
  text: string,
  auth: AuthContextProps,
  attachments?: string[]
): Promise<SessionReplyResponse> => {
  const headers = getApiAuthHeaders(auth);

  const payload: { text: string; attachments?: string[] } = { text };
  if (attachments && attachments.length > 0) {
    payload.attachments = attachments;
  }

  const response = await fetch(`${getBaseUrl()}/session/${sessionId}/reply`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to send reply: ${response.status}`);
  }

  return response.json();
};

export interface LiveTokenResponse {
  token: string;
  model: string;
  api_version: string;
  ws_url: string;
}

export interface TranscriptIngestRequest {
  segment_id: string;
  text: string;
  started_at?: string;
  ended_at?: string;
  is_final?: boolean;
  speaker_label?: string;
  source?: string;
}

export interface TranscriptIngestResponse {
  status: string;
  awaiting_user_input?: boolean;
  open_threads_count?: number;
  question_issued?: boolean;
  current_question?: string;
  memory?: InterrogationMemory;
}

export const mintLiveToken = async (
  sessionId: string,
  auth: AuthContextProps
): Promise<LiveTokenResponse> => {
  const headers = getApiAuthHeaders(auth);
  const response = await fetch(`${getBaseUrl()}/session/${sessionId}/live-token`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to mint live token: ${response.status}`);
  }
  return response.json();
};

export const submitTranscriptSegment = async (
  sessionId: string,
  segment: TranscriptIngestRequest,
  auth: AuthContextProps
): Promise<TranscriptIngestResponse> => {
  const headers = getApiAuthHeaders(auth);
  const response = await fetch(`${getBaseUrl()}/session/${sessionId}/transcript`, {
    method: 'POST',
    headers,
    body: JSON.stringify(segment),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to submit transcript: ${response.status}`);
  }
  return response.json();
};

export const diarizeAudio = async (
  sessionId: string,
  pcmBase64: string,
  auth: AuthContextProps
): Promise<{ segments: Array<{ speaker: string; start_seconds?: number; text: string }> }> => {
  const headers = getApiAuthHeaders(auth);
  const response = await fetch(`${getBaseUrl()}/session/${sessionId}/diarize`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ pcm_base64: pcmBase64 }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to diarize audio: ${response.status}`);
  }
  return response.json();
};

export const updateSpeakerRegistry = async (
  sessionId: string,
  speakerRegistry: Record<string, string>,
  auth: AuthContextProps
): Promise<{ speaker_registry: Record<string, string> }> => {
  const headers = getApiAuthHeaders(auth);
  const response = await fetch(`${getBaseUrl()}/session/${sessionId}/speaker-registry`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ speaker_registry: speakerRegistry }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to update speaker registry: ${response.status}`);
  }
  return response.json();
};

/**
 * Get list of user's previous sessions.
 * GET /sessions
 */
export const getUserSessions = async (
  auth: AuthContextProps
): Promise<SessionsListResponse> => {
  const headers = getApiAuthHeaders(auth);

  const response = await fetch(`${getBaseUrl()}/sessions`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch sessions: ${response.status}`);
  }

  return response.json();
};

/**
 * List available templates.
 * GET /templates
 */
export const listTemplates = async (
  auth: AuthContextProps
): Promise<TemplatesListResponse> => {
  const headers = getApiAuthHeaders(auth);

  const response = await fetch(`${getBaseUrl()}/templates`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch templates: ${response.status}`);
  }

  return response.json();
};

/**
 * Get full session details including plan and messages.
 * GET /session/{session_id}
 */
export const getSessionDetails = async (
  sessionId: string,
  auth: AuthContextProps
): Promise<SessionDetails> => {
  const headers = getApiAuthHeaders(auth);

  const response = await fetch(`${getBaseUrl()}/session/${sessionId}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch session details: ${response.status}`);
  }

  return response.json();
};

/**
 * Delete a session.
 * DELETE /session/{session_id}
 */
export const deleteSession = async (
  sessionId: string,
  auth: AuthContextProps
): Promise<{ message: string }> => {
  const headers = getApiAuthHeaders(auth);

  const response = await fetch(`${getBaseUrl()}/session/${sessionId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to delete session: ${response.status}`);
  }

  return response.json();
};

/**
 * Get presigned URL for file upload.
 * GET /s3-presigned?filename=filename.ext
 */
export const getPresignedUploadUrl = async (
  filename: string,
  auth: AuthContextProps
): Promise<{ upload_url: string; key: string }> => {
  const headers = getApiAuthHeaders(auth);
  
  const response = await fetch(`${getBaseUrl()}/s3-presigned?filename=${encodeURIComponent(filename)}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to get presigned URL: ${response.status}`);
  }

  return response.json();
};

/**
 * Upload file to S3 using presigned URL.
 * PUT to presigned URL
 */
export const uploadFileToS3 = async (
  presignedUrl: string,
  file: File
): Promise<void> => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file to S3: ${response.status}`);
  }
};

// ---------------------------------------------------------------------------
// Known-issue match payload
// ---------------------------------------------------------------------------
// Surfaced inline by the chat reply path: `process_user_reply` runs the
// deterministic regex match against known_issues.json before calling the
// LLM evaluator and persists any hits as a structured KNOWN_ISSUE chat
// message. The FE renders these as diagnostic cards (cause / resolution /
// log_locations) inline with the bot's evaluation reply.

export interface KnownIssueMatch {
  issue_id?: string;
  issue?: string;
  cause?: string;
  resolution?: string;
  log_locations?: string[];
  matched_pattern?: string;
  related_procedure?: string | null;
}

// ---------------------------------------------------------------------------
// Free-text doc search (semantic, embeddings-backed)
// POST /session/{session_id}/search-docs
// ---------------------------------------------------------------------------

export interface SearchDocsResult {
  title?: string;
  path?: string;
  kind?: string;
  applies_to?: string[];
  score: number;
  preview?: string;
}

export interface SearchDocsResponse {
  query: string;
  results: SearchDocsResult[];
}

export const searchDocs = async (
  sessionId: string,
  query: string,
  auth: AuthContextProps,
  opts?: { topK?: number; filterKind?: string }
): Promise<SearchDocsResponse> => {
  const headers = getApiAuthHeaders(auth);
  const body: Record<string, unknown> = { query };
  if (opts?.topK) body.top_k = opts.topK;
  if (opts?.filterKind) body.filter_kind = opts.filterKind;
  const resp = await fetch(`${getBaseUrl()}/session/${sessionId}/search-docs`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `search-docs failed: ${resp.status}`);
  }
  return resp.json();
};

// ---------------------------------------------------------------------------
// Structured installation report (rendered in /sessions/:id/report)
// GET /session/{session_id}/report
// ---------------------------------------------------------------------------

export interface ReportEnvironmentItem {
  label: string;
  value: string;
}

export interface ReportEnvironmentGroup {
  category: string;
  items: ReportEnvironmentItem[];
}

export interface ReportPrerequisite {
  item: string;
  rationale: string;
}

export interface ReportProcedureStep {
  number: number;
  title: string;
  description: string;
  command: string;
  expected_outcome: string;
  evidence: string;
  image_analysis: string;
  started_at: string;
  completed_at: string;
  duration_seconds: number | null;
  duration_human: string;
  completed_by: string;
  status: string;
  confidence: 'HIGH' | 'LOW' | string;
  source: string;
  references: string[];
}

export interface ReportValidationCheck {
  check: string;
  how_to_verify: string;
  pass_criteria: string;
}

export interface ReportPostInstallTask {
  task: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW' | string;
}

export interface ReportTroubleshootingItem {
  symptom: string;
  likely_cause: string;
  remediation: string;
}

export interface ReportReference {
  title: string;
  url: string;
  fetched: boolean;
  /** True for real http(s) links (clickable). False for `corpus://...`
   *  pseudo-paths into the bundled docs — those render as plain text. */
  is_url?: boolean;
}

export interface ReportSessionMetrics {
  started_at: string;
  completed_at: string;
  duration_seconds: number;
  duration_human: string;
  step_count: number;
  completed_steps: number;
  low_confidence_steps: number;
}

export interface ReportWarning {
  kind: 'LOW_CONFIDENCE' | 'GENERAL_KNOWLEDGE' | 'OVERRIDE' | 'GROUNDING_FAILED' | 'CONTRADICTION' | string;
  step_number: number | null;
  message: string;
}

export interface ReportSignOff {
  prepared_by: string;
  reviewed_by: string;
  approved_by: string;
  date: string;
}

export interface StructuredReport {
  schema_version: number;
  report_id: string;
  title: string;
  subtitle: string;
  /** When "interrogation", the report was built from expert-interview memory. */
  report_type?: 'guided' | 'interrogation';
  /** True for templates that produce a per-customer fillable engagement
   *  document (Tableau Server install, Tableau Server tuning, ...). False
   *  for generic session reports. The FE hides the customer-name row and
   *  the per-customer cover note in that case. Defaults to false on older
   *  payloads. */
  is_customer_template?: boolean;
  customer_placeholder: string;
  /** Section 5 / section 7 headings, supplied by the backend per template
   *  so e.g. a tuning report renders "Tuning Procedure" / "Post-Tuning
   *  Tasks" instead of the install-specific labels. Falls back to neutral
   *  defaults when missing (older payloads). */
  procedure_label?: string;
  post_label?: string;
  generated_at: string;
  generated_by: string;
  session_id: string;
  template_id: string;
  corpus_version: string;
  doc_version: string;
  status: string;
  executive_summary: string;
  scope: string;
  out_of_scope: string;
  audience: string;
  prerequisites: ReportPrerequisite[];
  procedure: ReportProcedureStep[];
  environment: ReportEnvironmentGroup[];
  validation: ReportValidationCheck[];
  post_install_tasks: ReportPostInstallTask[];
  troubleshooting: ReportTroubleshootingItem[];
  references: ReportReference[];
  warnings: ReportWarning[];
  session_metrics: ReportSessionMetrics;
  sign_off: ReportSignOff;
  raw_markdown: string;
  /** RDF/Turtle knowledge graph extracted during an interrogation session. */
  interrogation_turtle?: string;
  interrogation_turn_count?: number;
  interrogation_segment_count?: number;
  interrogation_claim_references?: Record<string, string[]>;
  interrogation_transcript_excerpt?: string;
}

export interface SessionReportResponse {
  report: StructuredReport;
}

export interface LlmCallDetail {
  call_id?: string;
  ts?: string;
  feature?: string;
  model?: string;
  prompt?: string;
  response?: string;
  truncated?: boolean;
  prompt_tokens?: number;
  output_tokens?: number;
  thinking_tokens?: number;
  total_tokens?: number;
  estimated_cost_usd?: number;
  turn_index?: number;
  chunk_index?: number;
  source_message_ts?: string;
  utterance_preview?: string;
}

export interface LlmCallDetailResponse {
  call: LlmCallDetail;
}

export const getLlmCallDetail = async (
  sessionId: string,
  callId: string,
  auth: AuthContextProps
): Promise<LlmCallDetail> => {
  const headers = getApiAuthHeaders(auth);
  const resp = await fetch(`${getBaseUrl()}/session/${sessionId}/llm-call/${callId}`, {
    method: 'GET',
    headers,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch LLM call detail: ${resp.status}`);
  }
  const json: LlmCallDetailResponse = await resp.json();
  return json.call;
};

export const getSessionReport = async (
  sessionId: string,
  auth: AuthContextProps
): Promise<StructuredReport> => {
  const headers = getApiAuthHeaders(auth);
  const resp = await fetch(`${getBaseUrl()}/session/${sessionId}/report`, {
    method: 'GET',
    headers,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch report: ${resp.status}`);
  }
  const json: SessionReportResponse = await resp.json();
  return json.report;
};

export const getRequirementsMatrix = async (
  auth: AuthContextProps
): Promise<RequirementsMatrix> => {
  const headers = getApiAuthHeaders(auth);
  const resp = await fetch(`${getBaseUrl()}/requirements-matrix`, {
    method: 'GET',
    headers,
  });
  if (!resp.ok) {
    throw new Error(`requirements-matrix failed: ${resp.status}`);
  }
  return resp.json();
};

export function validateResourcesAgainstMatrix(
  resourcesText: string,
  os: 'windows' | 'linux' | 'cross_platform',
  matrix: RequirementsMatrix
): ResourceWarning[] {
  if (!resourcesText) return [];
  const text = resourcesText.toLowerCase();


  const userRamMatch = text.match(/(\d+)\s*g(?:i)?b\s*(?:of\s*)?(?:ram|memory)/);
  const userCoreMatch = text.match(/(\d+)[\s-]*(?:v?cpu|core)/);
  const userDiskMatch = text.match(/(\d+)\s*g(?:i)?b\s*(?:of\s*)?(?:disk|ssd|nvme|hdd|storage)/);
  const userGhzMatch = text.match(/(\d+(?:\.\d+)?)\s*ghz/);
  const userRam = userRamMatch ? parseFloat(userRamMatch[1]) : null;
  const userCores = userCoreMatch ? parseFloat(userCoreMatch[1]) : null;
  const userDisk = userDiskMatch ? parseFloat(userDiskMatch[1]) : null;
  const userGhz = userGhzMatch ? parseFloat(userGhzMatch[1]) : null;

  const warnings: ResourceWarning[] = [];

  if (userRam !== null && userRam < TABLEAU_PRODUCTION_MINIMUMS.ram_gb) {
    warnings.push({
      field: 'ram_gb', user_value: userRam, doc_min_seen: TABLEAU_PRODUCTION_MINIMUMS.ram_gb,
      message: `You said ${userRam} GB RAM; Tableau Server's production minimum is ${TABLEAU_PRODUCTION_MINIMUMS.ram_gb} GB per node.`,
    });
  }
  if (userCores !== null && userCores < TABLEAU_PRODUCTION_MINIMUMS.cores) {
    warnings.push({
      field: 'cores', user_value: userCores, doc_min_seen: TABLEAU_PRODUCTION_MINIMUMS.cores,
      message: `You said ${userCores} cores; Tableau Server's production minimum is ${TABLEAU_PRODUCTION_MINIMUMS.cores} cores.`,
    });
  }
  if (userDisk !== null && userDisk < TABLEAU_PRODUCTION_MINIMUMS.disk_gb) {
    warnings.push({
      field: 'disk_gb', user_value: userDisk, doc_min_seen: TABLEAU_PRODUCTION_MINIMUMS.disk_gb,
      message: `You said ${userDisk} GB disk; Tableau Server needs at least ${TABLEAU_PRODUCTION_MINIMUMS.disk_gb} GB free for installation.`,
    });
  }
  if (userGhz !== null && userGhz < TABLEAU_PRODUCTION_MINIMUMS.ghz) {
    warnings.push({
      field: 'ghz', user_value: userGhz, doc_min_seen: TABLEAU_PRODUCTION_MINIMUMS.ghz,
      message: `You said ${userGhz} GHz; Tableau Server requires CPUs at ${TABLEAU_PRODUCTION_MINIMUMS.ghz} GHz or higher.`,
    });
  }

  void matrix;
  void os;

  return warnings;
}
